import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Bar } from 'react-chartjs-2'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { fetchTournamentWinLoss } from 'actions/results-actions'
import FilterDropdown from 'components/generic/FilterDropdown'
import i18n from 'constants/strings'
import styles from './tournament-chart.module.scss'
import useWindowSize from '../../../hooks/use-windowSize'
import { matchTypes, TABLET_BREAKPOINT } from 'constants/global'
import { tournamentChartoptions } from './TournamentChartOptions'
import MobileFilter from '../../generic/MobileFilterSelector/MobileFilterSelector'
import WTNSpinner from 'components/generic/WTNSpinner'
import {
  setTournamentChartYearFilter,
  setTournamentChartGameTypeFilter,
} from 'actions/results-actions'
import BtnWithArrow from 'components/generic/BtnWithArrow'
import { getLabels, getData } from './TournamentChartUtilities'
import produce from 'immer'
import NoDataContainer from 'components/generic/NoDataContainer'
import { GameType } from '../ResultsDoughnutChart/ResultsDoughnutChartTypes'
import { MatchUpTypeEnum } from 'types/global'

const MIN_YEAR_SELECTION = 2016
const CHART_MIN_Y_AXIS_TICKS = 10

interface Props {
  playerId: string
  title?: string
  lastPlayedSingles?: string
  lastPlayedDoubles?: string
}

const TournamentChart: React.FC<Props> = ({
  title,
  playerId,
  lastPlayedSingles,
  lastPlayedDoubles,
}) => {
  const dispatch = useDispatch()
  const { defaultYearFilterValue, defaultGameTypeFilterValue } = useMemo(() => {
    if (lastPlayedSingles)
      return {
        defaultYearFilterValue: new Date(lastPlayedSingles).getFullYear(),
        defaultGameTypeFilterValue: MatchUpTypeEnum.SINGLES,
      }
    if (lastPlayedDoubles)
      return {
        defaultYearFilterValue: new Date(lastPlayedDoubles).getFullYear(),
        defaultGameTypeFilterValue: MatchUpTypeEnum.DOUBLES,
      }

    return {
      defaultYearFilterValue: new Date().getFullYear(),
      defaultGameTypeFilterValue: MatchUpTypeEnum.SINGLES,
    }
  }, [lastPlayedSingles, lastPlayedDoubles])
  const data = useSelector((state: AppState) => state.results.tournWinLoss)
  const loading = useSelector((state: AppState) => state.results.tournWinLossLoading)
  const yearFilterValue = useSelector((state: AppState) => state.results.tournWinLossYearFilter)
  const gameTypeFilterValue = useSelector(
    (state: AppState) => state.results.tournWinLossGameTypeFilter
  )
  const yearFilter = yearFilterValue ?? defaultYearFilterValue
  const gameTypeFilter = gameTypeFilterValue ?? defaultGameTypeFilterValue
  const fetchData = useCallback(
    (year: number, matchType: GameType) =>
      dispatch(fetchTournamentWinLoss(playerId, year, matchType)),
    [playerId, dispatch]
  )
  const updateWinLossGraphYearFilter = useCallback(
    (year: number) => dispatch(setTournamentChartYearFilter(year)),
    [dispatch]
  )
  const updateWinLossGraphGameTypeFilter = useCallback(
    (gameType: GameType) => dispatch(setTournamentChartGameTypeFilter(gameType)),
    [dispatch]
  )
  const years = useMemo(() => {
    const date = new Date()
    const years: number[] = []
    while (date.getFullYear() >= MIN_YEAR_SELECTION) {
      years.push(date.getFullYear())
      date.setFullYear(date.getFullYear() - 1)
    }
    return years.map(y => ({ key: y, label: `${y}` }))
  }, [])

  const [dataset, setDataset] = useState<{ labels: string[]; datasets: any }>({
    labels: [],
    datasets: [],
  })

  const windowWidth = useWindowSize().width
  const isDesktop = windowWidth && windowWidth > TABLET_BREAKPOINT
  const [selectedDatasetHalfMobile, setSelectedDataSetHalfMobile] = useState<
    'first-half' | 'second-half'
  >('first-half')

  const highestWinsLoss = dataset.datasets
    .map(x => Math.max.apply(Math, x.data))
    .reduce((max, val) => (max > val ? max : val), 0)

  useEffect(() => {
    fetchData(yearFilter, gameTypeFilter)
  }, [fetchData, yearFilter, gameTypeFilter])

  useEffect(() => {
    const labels = getLabels()

    if (data && data.length) {
      if (isDesktop) {
        setDataset({
          labels,
          datasets: [
            {
              label: 'Wins',
              data: data && getData(data, 'matchesWon'),
              backgroundColor: '#E03E52',
              categoryPercentage: 0.7,
              barPercentage: 1.0,
            },
            {
              label: 'Losses',
              data: data && getData(data, 'matchesLost'),
              backgroundColor: '#003A70',
              categoryPercentage: 0.7,
              barPercentage: 1.0,
            },
          ],
        })
      } else {
        const xAxisTicksLength = 6

        const mobileLabels =
          selectedDatasetHalfMobile === 'first-half'
            ? labels?.slice(0, xAxisTicksLength)
            : labels?.slice(xAxisTicksLength, 12)

        const wins =
          selectedDatasetHalfMobile === 'first-half'
            ? getData(data, 'matchesWon').slice(0, xAxisTicksLength)
            : getData(data, 'matchesWon').slice(xAxisTicksLength, 12)

        const losses =
          selectedDatasetHalfMobile === 'first-half'
            ? getData(data, 'matchesLost').slice(0, xAxisTicksLength)
            : getData(data, 'matchesLost').slice(xAxisTicksLength, 12)

        setDataset({
          labels: mobileLabels,
          datasets: [
            {
              label: 'Wins',
              data: wins,
              backgroundColor: '#E03E52',
              categoryPercentage: 0.7,
              barPercentage: 1.0,
            },
            {
              label: 'Losses',
              data: losses,
              backgroundColor: '#003A70',
              categoryPercentage: 0.7,
              barPercentage: 1.0,
            },
          ],
        })
      }
    }
  }, [selectedDatasetHalfMobile, setSelectedDataSetHalfMobile, data, setDataset, isDesktop])

  if (!data) {
    return (
      <>
        {title && <h2>{title}</h2>}
        <NoDataContainer>
          <p>{i18n.t('strCharts.noDataGeneric')}</p>
        </NoDataContainer>
      </>
    )
  }

  return (
    <>
      {title && <h2>{title}</h2>}
      {isDesktop ? (
        <>
          <FilterDropdown
            options={years}
            selectedKey={yearFilter}
            onSelect={({ key }) => {
              updateWinLossGraphYearFilter(key)
            }}
          />
          <FilterDropdown
            options={matchTypes}
            selectedKey={gameTypeFilter}
            onSelect={({ key }) => updateWinLossGraphGameTypeFilter(key)}
            style={{ marginLeft: '20px' }}
          />
          {data && !(data.length > 0) ? (
            <NoDataContainer>
              <p>{i18n.t('strCharts.noDataTournaments')}</p>
            </NoDataContainer>
          ) : (
            <div className={styles.renderedChartContainer}>
              {loading && <WTNSpinner width={100} />}
              {!loading &&
                (highestWinsLoss > CHART_MIN_Y_AXIS_TICKS ? (
                  <Bar
                    data={dataset}
                    options={produce(tournamentChartoptions, chartoptions => {
                      chartoptions.scales.yAxes[0].ticks['max'] = highestWinsLoss
                    })}
                  ></Bar>
                ) : (
                  <Bar data={dataset} options={tournamentChartoptions}></Bar>
                ))}
            </div>
          )}
        </>
      ) : (
        <>
          <MobileFilter
            yearFilterData={{
              yearItems: years,
              selectedYearItem: yearFilter,
              yearDispatch: updateWinLossGraphYearFilter,
            }}
            gameTypeFilterData={{
              gameTypeItems: matchTypes,
              selectedGameTypeItem: gameTypeFilter,
              gameTypeDispatch: updateWinLossGraphGameTypeFilter,
            }}
            classNames={{ filterBar: styles.filterBar }}
          />

          {data && !(data.length > 0) ? (
            <NoDataContainer>
              <p>{i18n.t('strCharts.noDataTournaments')}</p>
            </NoDataContainer>
          ) : (
            <div className={styles.renderedChartContainer}>
              {loading && <WTNSpinner width={100} />}
              {!loading &&
                (highestWinsLoss > CHART_MIN_Y_AXIS_TICKS ? (
                  <Bar
                    data={dataset}
                    options={produce(tournamentChartoptions, chartoptions => {
                      chartoptions.scales.yAxes[0].ticks['max'] = highestWinsLoss
                    })}
                  ></Bar>
                ) : (
                  <Bar data={dataset} options={tournamentChartoptions}></Bar>
                ))}
            </div>
          )}
          {data && data.length > 0 && (
            <div className={styles.btnGroup}>
              <BtnWithArrow
                onClick={() => setSelectedDataSetHalfMobile('first-half')}
                direction={'left'}
                isDisabled={selectedDatasetHalfMobile === 'first-half'}
              >
                Previous
              </BtnWithArrow>
              <BtnWithArrow
                onClick={() => setSelectedDataSetHalfMobile('second-half')}
                direction={'right'}
                isDisabled={selectedDatasetHalfMobile === 'second-half'}
              >
                Next
              </BtnWithArrow>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default TournamentChart
